import React, { useState, useEffect } from "react";
import { Link } from "react-scroll";
import { Helmet } from "react-helmet";
import "./App.css";
import VehicleSection from "./vehicle";
import Navbar from "./navbar";
import NewestCarFleet from "./new";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCar,
  faUserShield,
  faSmile,
  faLock,
  faPlaneDeparture,
  faTaxi,
  faGifts,
  faRoute,
  faGem,
  faUsers,
  faMapMarkedAlt,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import { faFacebook, faWhatsapp, faInstagram } from "@fortawesome/free-brands-svg-icons";

/**
 * Popup - call / whatsapp CTA modal
 * accessible, simple, animated
 */
const Popup = ({ onClose }) => (
  <div
    role="dialog"
    aria-modal="true"
    aria-labelledby="contact-title"
    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn"
  >
    <div className="bg-white rounded-lg shadow-2xl p-6 text-center max-w-sm w-full transform animate-scaleUp">
      <h2 id="contact-title" className="text-lg font-bold mb-4 text-red-500">
        Get in Touch
      </h2>
      <p className="mb-6 text-gray-700">
        Book your ride now — reliable car rental services in Ranchi (local, outstation & airport transfers).
      </p>
      <div className="flex justify-around mb-6">
        <a
          href="tel:+916205499309"
          className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 shadow-lg hover:shadow-xl flex items-center space-x-2 transition-all"
          aria-label="Call Shiv Shakti Travels"
        >
          <FontAwesomeIcon icon={faPhone} size="lg" />
          <span>Call Now</span>
        </a>
        <a
          href="https://wa.me/+916205499309"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 shadow-lg hover:shadow-xl flex items-center space-x-2 transition-all"
          aria-label="WhatsApp Shiv Shakti Travels"
        >
          <FontAwesomeIcon icon={faWhatsapp} size="lg" />
          <span>WhatsApp</span>
        </a>
      </div>
      <button onClick={onClose} className="text-red-500 font-bold hover:underline" aria-label="Close popup">
        Close
      </button>
    </div>
  </div>
);

const App = () => {
  const [isPopupVisible, setIsPopupVisible] = useState(true);

  /**
   * Count-up animation for elements with .animate-count
   * Starts when elements come into view.
   */
  useEffect(() => {
    const counters = document.querySelectorAll(".animate-count");
    const speed = 60; // higher = slower

    if (!("IntersectionObserver" in window)) {
      // Fallback: just set to final values
      counters.forEach((el) => (el.innerText = el.getAttribute("data-count")));
      return;
    }

    const countUp = (el) => {
      const target = +el.getAttribute("data-count") || 0;
      const update = () => {
        const current = +el.innerText || 0;
        const inc = Math.ceil(target / speed);
        if (current < target) {
          el.innerText = Math.min(target, current + inc);
          setTimeout(update, 20);
        } else {
          el.innerText = target;
        }
      };
      update();
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            countUp(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );

    counters.forEach((c) => observer.observe(c));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="App font-sans antialiased text-gray-900">
      <Helmet>
        {/* Primary SEO */}
        <title>Shiv Shakti Travels | Car Rental Service in Ranchi — Local, Outstation & Airport Cabs</title>
        <meta
          name="description"
          content="Shiv Shakti Travels - Best car rental in Ranchi. Book local taxis, outstation cabs, airport transfers, wedding cars, and luxury vehicles. Reliable drivers, transparent pricing."
        />
        <meta
          name="keywords"
          content="car rental Ranchi, taxi service Ranchi, outstation cab Ranchi, airport transfer Ranchi, wedding car rental Ranchi, luxury car hire Ranchi"
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta charSet="utf-8" />
        <link rel="canonical" href="https://shivshaktitravels.in" />

        {/* Structured Data: LocalBusiness */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: "Shiv Shakti Travels",
            image: ["https://shivshaktitravels.in/logo.png"],
            "@id": "https://shivshaktitravels.in",
            url: "https://shivshaktitravels.in",
            telephone: "+916205499309",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Kokar",
              addressLocality: "Ranchi",
              addressRegion: "Jharkhand",
              postalCode: "834001",
              addressCountry: "IN",
            },
            priceRange: "₹₹",
            description:
              "Car rental service in Ranchi offering local rental, outstation, airport transfers, wedding cars, and luxury vehicles.",
            openingHours: "Mo,Tu,We,Th,Fr,Sa,Su 00:00-23:59",
          })}
        </script>
      </Helmet>

      {isPopupVisible && <Popup onClose={() => setIsPopupVisible(false)} />}

      <Navbar />

      {/* HERO */}
      <header
  id="home"
  className="relative h-[70vh] flex items-center justify-center text-center shadow-lg bg-cover bg-center"
  style={{
    backgroundImage: "linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.35)), url('/sst.png')",
    backgroundSize: "cover",
    backgroundPosition: "center",
  }}
>
  <div className="max-w-3xl w-full mx-4 bg-transparent  rounded-xl p-8 shadow-2xl animate-fadeInUp">
    <h1 className="text-4xl md:text-5xl font-extrabold mb-4 text-red-600 drop-shadow-lg">
      Shiv Shakti Travels
    </h1>
    <p className="text-lg md:text-xl text-white-800 mb-6 font-medium">
      Trusted car rental in Ranchi — local taxis, outstation cabs, airport transfers & wedding cars.
    </p>
    <div className="flex items-center justify-center gap-4">
      <Link
        to="vehicle"
        smooth
        duration={600}
        className="bg-red-600 text-white py-3 px-6 rounded-lg hover:bg-red-700 hover:shadow-xl transition duration-300 transform hover:scale-105"
        aria-label="Book vehicle"
      >
        Book Now
      </Link>
      <a
        href="tel:+916205499309"
        className="border border-red-600 text-red-600 py-3 px-6 rounded-lg hover:bg-red-50 transition duration-300 transform hover:scale-105"
        aria-label="Call now"
      >
        +91 62054 99309
      </a>
    </div>
  </div>
</header>


      {/* NEWEST FLEET */}
     
          <NewestCarFleet />
     

      {/* SERVICES */}
      <section id="services" className="bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-red-500 mb-8">
            Our Car Rental Services in Ranchi
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: faRoute,
                title: "Round Trip Cabs",
                desc: "Affordable roundtrip cab booking in Ranchi for any destination.",
              },
              { icon: faTaxi, title: "Local Rental", desc: "Hourly rental taxis to explore Ranchi city with comfort." },
              {
                icon: faPlaneDeparture,
                title: "Airport Transfers",
                desc: "On-time airport pickup & drop services in Ranchi.",
              },
              { icon: faGifts, title: "Wedding Cars", desc: "Luxury wedding cars & decorated vehicles for special days." },
              { icon: faCar, title: "Tourist Packages", desc: "Customizable tourist taxi packages to Jharkhand’s best spots." },
              { icon: faGem, title: "Luxury Cars", desc: "Premium luxury cars for VIP & corporate clients." },
              { icon: faUsers, title: "Convoy Packages", desc: "Multiple vehicles for large events & group travel." },
              { icon: faMapMarkedAlt, title: "Custom Itineraries", desc: "Personalized travel plans to match your journey." },
            ].map((service, i) => (
              <article
                key={i}
                className="bg-white p-6 rounded-2xl border border-red-100 shadow-md hover:shadow-2xl transform hover:scale-105 transition-all will-change-transform"
                tabIndex={0}
                aria-labelledby={`service-${i}`}
              >
                <div className="flex items-center justify-center mb-4">
                  <FontAwesomeIcon icon={service.icon} size="3x" className="text-red-500" />
                </div>
                <h3 id={`service-${i}`} className="text-xl font-semibold mb-2 text-gray-900">
                  {service.title}
                </h3>
                <p className="text-gray-600">{service.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* VEHICLE SECTION */}
      <section id="vehicle" className="py-12">
        <div className="container mx-auto px-4">
          <VehicleSection />
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-red-500 mb-8">Why Choose Shiv Shakti Travels</h2>
          <p className="text-center text-gray-700 max-w-2xl mx-auto mb-8">
            Reliable drivers • Transparent pricing • Clean vehicles • 24/7 support — trusted by hundreds of happy
            customers in Ranchi.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: faCar, title: "Well-Maintained Cars", desc: "Wide range of well-maintained & clean cars." },
              { icon: faUserShield, title: "Verified Drivers", desc: "Experienced drivers for hills & highways." },
              { icon: faSmile, title: "Customer Satisfaction", desc: "High ratings & repeat customers." },
              { icon: faLock, title: "Privacy & Safety", desc: "Private hires with safety-first approach." },
            ].map((item, idx) => (
              <div
                key={idx}
                className="p-6 text-center border-2 border-red-50 rounded-2xl shadow-sm hover:shadow-lg transition"
              >
                <FontAwesomeIcon icon={item.icon} size="3x" className="text-red-500 mb-4" />
                <h4 className="text-xl font-semibold mb-2">{item.title}</h4>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      <section id="packages" className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-red-500 mb-8">Popular Packages & Day Tours</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: "Dewri Mandir Package", desc: "One day tour (Sedan / SUV).", price: "₹2800/3400" },
              { title: "Netrahat Tour", desc: "3 days 2 nights (Sedan/SUV).", price: "₹9750/11900" },
              { title: "Rajrappa Mandir", desc: "One day tour (Sedan / SUV).", price: "₹2800/3400" },
              { title: "City Tour", desc: "8 hours, 80 km (Sedan/SUV).", price: "₹2,000/2400/3000" },
              { title: "Patratu Valley", desc: "Nature day trip (Sedan/SUV).", price: "₹2000/2700" },
              { title: "Ranchi To Deoghar", desc: "2 days 1 night (Sedan/SUV).", price: "₹11000/12500" },
            ].map((pkg, i) => (
              <article
                key={i}
                className="bg-white p-6 rounded-2xl border border-red-100 shadow-md hover:shadow-2xl transform hover:-translate-y-1 transition"
              >
                <h3 className="text-xl font-semibold mb-2">{pkg.title}</h3>
                <p className="text-gray-600 mb-4">{pkg.desc}</p>
                <div className="flex items-center justify-between">
                  <p className="text-red-600 font-bold">{pkg.price}</p>
                  <a
                    href="tel:+916205499309"
                    className="text-sm underline text-red-500"
                    aria-label={`Book ${pkg.title}`}
                  >
                    Book Now
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* DESTINATIONS */}
      <section id="destination" className="py-12">
  <div className="container mx-auto px-4">
    <h2 className="text-3xl md:text-4xl font-bold text-center text-red-500 mb-8">
      Top Destinations from Ranchi
    </h2>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {[
        "Ranchi to Ramgarh",
        "Ranchi to Tarapith",
        "Ranchi to Dumka",
        "Ranchi to Deoghar",
        "Ranchi to Raipur",
        "Ranchi to Rourkela",
        "Ranchi to Jamshedpur",
        "Ranchi to Hazaribagh",
        "Ranchi to Bokaro",
        "Ranchi to Kolkata",
        "Ranchi to Patna",
        "Ranchi to Varanasi",
        "Ranchi to Bhubaneswar",
        "Ranchi to Dhanbad",
        "Ranchi to Gaya",
        "Ranchi to Siliguri",
        "Ranchi to Puri",
        "Ranchi to Kanpur",
      ].map((dest, i) => (
        <div
          key={i}
          className="p-6 border border-red-100 rounded-2xl shadow-sm bg-white hover:shadow-lg hover:scale-[1.02] transform transition duration-300 ease-in-out"
        >
          <h3 className="text-lg font-semibold text-gray-800">{dest}</h3>
          <p className="text-sm text-gray-600 mt-2">
            Comfortable cab service for one-way & round trips.
          </p>
        </div>
      ))}
    </div>
  </div>
</section>


      {/* FOOTER */}
      <footer
  id="footer"
  className="bg-gradient-to-t from-red-50 to-white text-black pt-12 pb-6 mt-12 border-t border-gray-200"
>
  <div className="container mx-auto px-4 grid md:grid-cols-3 gap-10 text-center md:text-left">
    
    {/* Company Info */}
    <div>
      <h4 className="font-bold text-xl mb-3 text-red-500">Shiv Shakti Travels</h4>
      <p className="text-gray-600 leading-relaxed">
        Reliable & affordable car rental service based in Ranchi. We ensure comfort, safety, and timely rides for every journey.
      </p>
      <div className="flex justify-center md:justify-start mt-5 space-x-4">
        <a
          href="https://facebook.com"
          target="_blank"
          rel="noreferrer"
          aria-label="Facebook"
          className="transform hover:scale-110 transition duration-300"
        >
          <FontAwesomeIcon icon={faFacebook} className="text-red-500 text-xl" />
        </a>
        <a
          href="https://www.instagram.com/shivshaktitravelcab/"
          target="_blank"
          rel="noreferrer"
          aria-label="Instagram"
          className="transform hover:scale-110 transition duration-300"
        >
          <FontAwesomeIcon icon={faInstagram} className="text-red-500 text-xl" />
        </a>
        <a
          href="https://wa.me/+916205499309"
          target="_blank"
          rel="noreferrer"
          aria-label="WhatsApp"
          className="transform hover:scale-110 transition duration-300"
        >
          <FontAwesomeIcon icon={faWhatsapp} className="text-green-500 text-xl" />
        </a>
      </div>
    </div>

    {/* Contact Info */}
    <div>
      <h4 className="font-bold text-xl mb-3 text-red-500">Contact</h4>
      <p className="text-gray-600">📍 Kokar, Ranchi, Jharkhand - 834001</p>
      <p className="text-gray-600 mt-1">
        📞{" "}
        <a
          href="tel:+916205499309"
          className="hover:text-red-500 transition-colors duration-200"
        >
          +91 62054 99309
        </a>
      </p>
      <p className="text-gray-600 mt-1">
        ✉️{" "}
        <a
          href="mailto:shivshaktitravelcab@gmail.com"
          className="hover:text-red-500 transition-colors duration-200"
        >
          shivshaktitravelcab@gmail.com
        </a>
      </p>
    </div>

    {/* Quick Links */}
    <div>
      <h4 className="font-bold text-xl mb-3 text-red-500">Quick Links</h4>
      <ul className="text-gray-600 space-y-2">
        <li>
          <Link
            to="home"
            smooth
            duration={500}
            className="hover:text-red-500 transition-colors duration-200 cursor-pointer"
          >
            Home
          </Link>
        </li>
        <li>
          <Link
            to="vehicle"
            smooth
            duration={500}
            className="hover:text-red-500 transition-colors duration-200 cursor-pointer"
          >
            Fleet
          </Link>
        </li>
        <li>
          <Link
            to="footer"
            smooth
            duration={500}
            className="hover:text-red-500 transition-colors duration-200 cursor-pointer"
          >
            Contact Us
          </Link>
        </li>
      </ul>
    </div>
  </div>

  {/* Bottom Bar */}
  <div className="text-center text-gray-500 text-sm mt-10 border-t border-gray-200 pt-4">
    &copy; {new Date().getFullYear()} Shiv Shakti Travels. All Rights Reserved.
  </div>
</footer>

    </div>
  );
};

export default App;
